import { ReactNode } from 'react';
import { CalendarClock, Package, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import dayjs from '@/dayjsSetup';
import { formatDateByDayjs, formatWithThousandSeparator } from '@/utils';
import type { MerchantPricingPlan } from '@/types';
import VolumeLadder from './VolumeLadder';

/** Card B of Settings > Pricing plan — where the number came from and when it
 *  moves next. Rendered only for LADDER plans; a custom plan has no ladder. */

const CARD = 'rounded-2xl border border-sd-border bg-sd-card shadow-sm';
const DATE_FORMAT = 'DD MMM YYYY';

interface DetailRowProps {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}

const DetailRow = ({ icon, label, value, hint }: DetailRowProps) => (
  <div className='flex items-start gap-3 py-4 first:pt-0 last:pb-0'>
    <span className='mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sd-muted text-sd-muted-foreground'>
      {icon}
    </span>
    <div className='min-w-0 flex-1'>
      <p className='m-0 text-xs font-medium text-sd-muted-foreground'>{label}</p>
      <p className='m-0 mt-0.5 text-sm font-semibold text-sd-foreground'>{value}</p>
      {hint ? <p className='m-0 mt-1 text-xs text-sd-muted-foreground'>{hint}</p> : null}
    </div>
  </div>
);

const ReviewCard = ({ data }: { data: MerchantPricingPlan }) => {
  const { t } = useTranslation();
  const volume = data.qualifying_volume;

  const inGrace = data.status === 'grace' || dayjs.utc(data.grace_until ?? '').isAfter(dayjs.utc());
  // The window is half-open [start, end): orders on the end date are NOT in
  // this count. Printing the raw end instant would name a day the merchant's
  // orders did not count towards, which is exactly the kind of number they
  // open a support ticket about.
  const windowEndInclusive = dayjs.utc(volume.window_end_at).subtract(1, 'day').toISOString();

  const statusNote =
    data.status === 'pending_downgrade'
      ? t('pricing_status_note_pending_downgrade')
      : inGrace
        ? t('pricing_status_note_grace')
        : null;

  return (
    <section className={CARD}>
      <div className='border-b border-sd-border px-5 py-4'>
        <h3 className='text-base font-semibold'>{t('how_your_rate_is_reviewed')}</h3>
        <p className='mt-0.5 text-xs text-sd-muted-foreground'>
          {t('how_your_rate_is_reviewed_hint')}
        </p>
      </div>

      <div className='p-5'>
        {statusNote ? (
          <div className='mb-4 rounded-xl border border-sd-border bg-sd-muted/40 p-3'>
            <p className='m-0 text-xs text-sd-foreground'>{statusNote}</p>
          </div>
        ) : null}

        <div className='divide-y divide-sd-border'>
          <DetailRow
            icon={<Package className='h-4 w-4' />}
            label={String(t('pricing_plan_qualifying_orders'))}
            value={<bdi dir='ltr'>{formatWithThousandSeparator(volume.orders)}</bdi>}
            hint={t('pricing_plan_volume_window', {
              from: formatDateByDayjs(volume.window_start_at, DATE_FORMAT),
              to: formatDateByDayjs(windowEndInclusive, DATE_FORMAT),
            })}
          />

          {/* BOXY-232 — the rung they are on, between the number that decides
              it and the date it is next read. Renders nothing when the service
              has no ladder. */}
          {data.volume_ladder?.length ? (
            <div className='py-4'>
              <VolumeLadder ladder={data.volume_ladder} orders={volume.orders} />
            </div>
          ) : null}

          <DetailRow
            icon={<CalendarClock className='h-4 w-4' />}
            label={String(t('pricing_plan_next_review'))}
            // A null next evaluation is a STATE, not a missing value, and WHICH
            // state depends on whether there is a protection period at all.
            // Saying "after your protection period ends" to an active merchant
            // who has none is a status-blind fallback telling them about a
            // thing that does not exist — the same shape as pairing a price
            // with someone else's band.
            value={
              data.next_evaluation_at
                ? formatDateByDayjs(data.next_evaluation_at, DATE_FORMAT)
                : data.grace_until
                  ? String(t('pricing_plan_next_review_after_protection'))
                  : String(t('pricing_plan_next_review_unscheduled'))
            }
            hint={t('pricing_plan_next_review_hint')}
          />

          {inGrace ? (
            <DetailRow
              icon={<ShieldCheck className='h-4 w-4' />}
              label={String(t('pricing_plan_rate_protected'))}
              value={
                // A null grace_until means the clock has not started: grace
                // runs from the merchant's FIRST order, not from the day they
                // were placed on the plan.
                data.grace_until
                  ? formatDateByDayjs(data.grace_until, DATE_FORMAT)
                  : t('pricing_plan_grace_starts_with_first_order')
              }
              hint={t('pricing_plan_rate_protected_hint')}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default ReviewCard;
